import { uiText } from '../../utils/i18n';
import { useState, useRef, useCallback, useEffect, useMemo, type KeyboardEvent, type MouseEvent } from 'react';
import type { ChatSession, WSOutgoingMessage, FileAttachment } from '../../types';
import { useStore } from '../../store';
import { useStreamingASR } from '../../hooks/useStreamingASR';
import { Avatar } from '../shared/Avatar';
import { getAvatar } from '../../utils/avatar-store';
import { generateId } from './helpers';
import styles from './InputBar.module.css';

interface InputBarProps {
  send: (msg: WSOutgoingMessage) => void;
  sendBinary: (data: ArrayBuffer | Uint8Array) => void;
  session: ChatSession;
  botName: string;
  isThinking?: boolean;
}

export function InputBar({ send, sendBinary, session, botName, isThinking }: InputBarProps) {
  const [text, setText] = useState('');
  const [pendingFiles, setPendingFiles] = useState<FileAttachment[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const addMessage = useStore((s) => s.addMessage);
  const connected = useStore((s) => s.connected);
  const groups = useStore((s) => s.groups);
  const bots = useStore((s) => s.bots);
  const composerDraft = useStore((s) => s.composerDrafts[session.id]);
  const clearComposerDraft = useStore((s) => s.clearComposerDraft);

  useEffect(() => {
    if (!composerDraft) return;
    setText(composerDraft);
    clearComposerDraft(session.id);
    window.requestAnimationFrame(() => textareaRef.current?.focus());
  }, [clearComposerDraft, composerDraft, session.id]);

  // Mention popup state
  const [showMention, setShowMention] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const mentionRef = useRef<HTMLDivElement>(null);

  // Get group members for mention
  const groupMembers = useMemo(() => {
    if (!session.groupId) return [];
    const group = groups.find((g) => g.id === session.groupId);
    if (!group) return [];
    return group.members;
  }, [session.groupId, groups]);

  const filteredMembers = useMemo(() => {
    if (!mentionFilter) return groupMembers;
    const q = mentionFilter.toLowerCase();
    return groupMembers.filter((m) => m.toLowerCase().includes(q));
  }, [groupMembers, mentionFilter]);

  const { startASR, stopASR, asrState, asrPartialText, supported: asrSupported } = useStreamingASR();

  const isRecording = asrState === 'recording' || asrState === 'connecting';

  // Listen for finalized ASR text and append to input
  useEffect(() => {
    const handler = (e: Event) => {
      const final = (e as CustomEvent).detail as string;
      if (final) setText((prev) => prev + final);
    };
    window.addEventListener('asr-final', handler);
    return () => window.removeEventListener('asr-final', handler);
  }, []);
  const insertMention = useCallback((name: string) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const cursor = ta.selectionStart;
    const before = text.slice(0, cursor);
    // Find the @ that triggered the mention popup
    const atIdx = before.lastIndexOf('@');
    const prefix = atIdx >= 0 ? text.slice(0, atIdx) : before;
    const suffix = text.slice(cursor);
    const newText = `${prefix}@${name} ${suffix}`;
    setText(newText);
    setShowMention(false);
    setMentionFilter('');
    setMentionIndex(0);
    // Restore cursor after @name + space
    requestAnimationFrame(() => {
      const pos = prefix.length + name.length + 2; // @ + name + space
      ta.setSelectionRange(pos, pos);
      ta.focus();
    });
  }, [text]);

  const [showEmoji, setShowEmoji] = useState(false);
  const emojiRef = useRef<HTMLDivElement>(null);

  const handleSend = useCallback(() => {
    const trimmed = text.trim();
    if ((!trimmed && pendingFiles.length === 0) || !connected) return;

    const messageId = generateId();

    // Build text with file references for the configured agent to read.
    let fullText = trimmed;
    if (pendingFiles.length > 0) {
      const filePaths = pendingFiles.map(f => f.path).join('\n');
      fullText = trimmed
        ? `${trimmed}\n\n[Attached files]\n${filePaths}`
        : `[Attached files]\n${filePaths}`;
    }

    addMessage(session.id, {
      id: `user-${messageId}`,
      type: 'user',
      // Attachments have their own visual cards. Keeping the actual typed text
      // separate avoids showing a filename twice for file-only messages.
      text: trimmed,
      timestamp: Date.now(),
      attachments: pendingFiles.length > 0 ? [...pendingFiles] : undefined,
    });

    if (botName.startsWith('dm:')) {
      // Direct message to another user
      const targetUserId = botName.slice(3);
      const token = localStorage.getItem('novax:token') || '';
      fetch(`/auth/dm/${encodeURIComponent(targetUserId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: fullText }),
      }).catch(() => { /* ignore */ });
    } else if (session.groupId) {
      send({
        type: 'group_chat',
        groupId: session.groupId,
        chatId: session.id,
        text: fullText,
        messageId,
      });
    } else {
      addMessage(session.id, {
        id: messageId,
        type: 'assistant',
        text: '',
        timestamp: Date.now(),
        state: {
          status: 'thinking',
          userPrompt: fullText,
          responseText: '',
          toolCalls: [],
        },
      });
      send({
        type: 'chat',
        botName,
        chatId: session.id,
        text: fullText,
        messageId,
      });
    }

    setPendingFiles([]);
    setText('');
    textareaRef.current?.focus();
  }, [text, pendingFiles, connected, session, botName, addMessage, send]);

  const handleStop = useCallback(() => {
    send({ type: 'stop', chatId: session.id });
  }, [send, session.id]);

  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setText(val);

    // Detect @ for mention popup in group chats
    if (session.groupId && groupMembers.length > 0) {
      const cursor = e.target.selectionStart;
      const before = val.slice(0, cursor);
      const atIdx = before.lastIndexOf('@');
      if (atIdx >= 0 && (atIdx === 0 || /\s/.test(before[atIdx - 1]))) {
        const query = before.slice(atIdx + 1);
        if (!query.includes(' ')) {
          setMentionFilter(query);
          setMentionIndex(0);
          setShowMention(true);
          return;
        }
      }
      setShowMention(false);
    }
  }, [session.groupId, groupMembers]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (showMention && filteredMembers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((i) => (i + 1) % filteredMembers.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((i) => (i - 1 + filteredMembers.length) % filteredMembers.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(filteredMembers[mentionIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowMention(false);
        return;
      }
    }

    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (isThinking) return;
      handleSend();
    }
  }, [handleSend, isThinking, showMention, filteredMembers, mentionIndex, insertMention]);

  const doUpload = useCallback(async (files: FileList, _label: string) => {
    const token = localStorage.getItem('novax:token') || '';
    for (const file of Array.from(files)) {
      try {
        const res = await fetch(
          `/api/upload?filename=${encodeURIComponent(file.name)}&chatId=${encodeURIComponent(session.id)}`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/octet-stream',
            },
            body: file,
          },
        );
        if (res.ok) {
          const data = await res.json();
          const url = `/api/files/${encodeURIComponent(session.id)}/${encodeURIComponent(data.filename)}`;
          setPendingFiles(prev => [...prev, {
            name: data.filename,
            type: file.type || 'application/octet-stream',
            size: data.size,
            url,
            path: data.path,
          }]);
        }
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }
  }, [session.id]);

  const removePendingFile = useCallback((index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleMediaClick = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = 'image/*,video/*';
    input.onchange = () => { if (input.files?.length) doUpload(input.files, 'Media'); };
    input.click();
  }, [doUpload]);

  const handleFileClick = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = () => { if (input.files?.length) doUpload(input.files, 'Attached files'); };
    input.click();
  }, [doUpload]);

  const handleEmojiSelect = useCallback((emoji: string) => {
    setText((prev) => prev + emoji);
    setShowEmoji(false);
    textareaRef.current?.focus();
  }, []);


  const handleVoiceClick = useCallback(() => {
    if (isRecording) {
      stopASR();
    } else {
      startASR();
    }
  }, [isRecording, startASR, stopASR]);

  const canSend = connected && (text.trim().length > 0 || pendingFiles.length > 0);

  return (
    <div className={styles.container}>
      <div className={styles.inputWrap}>
        {/* Pending files preview strip */}
        {pendingFiles.length > 0 && (
          <div className={styles.pendingStrip}>
            {pendingFiles.map((f, i) => {
              const isImage = f.type.startsWith('image/');
              return (
                <div key={i} className={styles.pendingItem}>
                  {isImage ? (
                    <img src={f.url} alt={f.name} className={styles.pendingThumb} />
                  ) : (
                    <div className={styles.pendingFileIcon}>
                      <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                    </div>
                  )}
                  <span className={styles.pendingName}>{f.name}</span>
                  <button
                    className={styles.pendingRemove}
                    onClick={() => removePendingFile(i)}
                    title="Remove"
                  >
                    <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ position: 'relative' }}>
          <textarea
            ref={textareaRef}
            className={styles.textarea}
            value={asrPartialText ? text + asrPartialText : text}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder={session.groupId ? uiText("输入消息，@agent 名字来提问") : `Message ${botName.startsWith('dm:') ? (session.title !== 'New Chat' ? session.title : 'User') : botName}`}
            rows={1}
            disabled={!connected}
          />
          {showMention && filteredMembers.length > 0 && (
            <div className={styles.mentionPopup} ref={mentionRef}>
              {filteredMembers.map((name, i) => {
                const av = getAvatar(name);
                const bot = bots.find((b) => b.name === name);
                return (
                  <button
                    key={name}
                    className={`${styles.mentionItem} ${i === mentionIndex ? styles.mentionItemActive : ''}`}
                    onMouseDown={(e) => { e.preventDefault(); insertMention(name); }}
                    onMouseEnter={() => setMentionIndex(i)}
                  >
                    <Avatar name={name} size="sm" imageUrl={av.imageUrl} presetColor={av.presetColor} />
                    <div className={styles.mentionInfo}>
                      <span className={styles.mentionName}>{name}</span>
                      {bot?.description && <span className={styles.mentionDesc}>{bot.description}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {session.groupId ? (
          <div className={styles.actionBar}>
            <div className={styles.actionLeft}>
              <button className={styles.iconBtn} onClick={handleMediaClick} title="Photo / Video">
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
              </button>
              <button className={styles.iconBtn} onClick={handleFileClick} title="Attach files">
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 11l-9 9a5 5 0 01-7-7l9-9a3.5 3.5 0 015 5l-9 9a2 2 0 01-3-3l8-8" />
                </svg>
              </button>
              <div style={{ position: 'relative' }} ref={emojiRef}>
                <button className={styles.iconBtn} onClick={() => setShowEmoji(!showEmoji)} title="Emoji">
                  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                    <line x1="9" y1="9" x2="9.01" y2="9" />
                    <line x1="15" y1="9" x2="15.01" y2="9" />
                  </svg>
                </button>
                {showEmoji && <EmojiPicker onSelect={handleEmojiSelect} onClose={() => setShowEmoji(false)} />}
              </div>

              {groupMembers.length > 0 && (
                <button
                  className={styles.iconBtn}
                  onClick={() => {
                    const ta = textareaRef.current;
                    if (ta) {
                      const cursor = ta.selectionStart;
                      const before = text.slice(0, cursor);
                      const after = text.slice(cursor);
                      const needSpace = before.length > 0 && !/\s$/.test(before);
                      const newText = before + (needSpace ? ' @' : '@') + after;
                      setText(newText);
                      setMentionFilter('');
                      setMentionIndex(0);
                      setShowMention(true);
                      requestAnimationFrame(() => {
                        const pos = before.length + (needSpace ? 2 : 1);
                        ta.setSelectionRange(pos, pos);
                        ta.focus();
                      });
                    }
                  }}
                  title={uiText("@提及")}
                >
                  <span style={{ fontSize: 14, fontWeight: 700 }}>@</span>
                </button>
              )}
            </div>

            <div className={styles.actionRight}>
              {asrSupported && (
                <button
                  className={`${styles.secondaryBtn} ${isRecording ? styles.voiceRecording : ''}`}
                  onClick={handleVoiceClick}
                  title={isRecording ? 'Stop recording' : 'Voice input'}
                >
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="3" width="6" height="12" rx="3" stroke={isRecording ? '#f1ecdf' : 'currentColor'} fill={isRecording ? '#f1ecdf' : 'none'} />
                    <path d="M5 11a7 7 0 0014 0M12 18v3" stroke={isRecording ? '#f1ecdf' : 'currentColor'} />
                  </svg>
                  <span>{isRecording ? 'Recording...' : 'Voice'}</span>
                </button>
              )}
              {isThinking ? (
                <button className={styles.stopBtn} onClick={handleStop} title="Stop">
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor">
                    <rect x="4" y="4" width="16" height="16" rx="2" />
                  </svg>
                </button>
              ) : (
                <button className={styles.sendBtn} onClick={handleSend} disabled={!canSend} title="Send">
                  <span>Send</span>
                  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.actionBar}>
            <div className={styles.actionLeft}>
              <button className={styles.iconBtn} onClick={handleMediaClick} title="Photo / Video">
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
              </button>
              <button className={styles.iconBtn} onClick={handleFileClick} title="Attach files">
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 11l-9 9a5 5 0 01-7-7l9-9a3.5 3.5 0 015 5l-9 9a2 2 0 01-3-3l8-8" />
                </svg>
              </button>
              <div style={{ position: 'relative' }} ref={emojiRef}>
                <button className={styles.iconBtn} onClick={() => setShowEmoji(!showEmoji)} title="Emoji">
                  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M8 14s1.5 2 4 2 4-2 4-2" />
                    <line x1="9" y1="9" x2="9.01" y2="9" />
                    <line x1="15" y1="9" x2="15.01" y2="9" />
                  </svg>
                </button>
                {showEmoji && <EmojiPicker onSelect={handleEmojiSelect} onClose={() => setShowEmoji(false)} />}
              </div>

            </div>
            <div className={styles.actionRight}>
              {asrSupported && (
                <button
                  className={`${styles.secondaryBtn} ${isRecording ? styles.voiceRecording : ''}`}
                  onClick={handleVoiceClick}
                  title={isRecording ? 'Stop recording' : 'Voice input'}
                >
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="3" width="6" height="12" rx="3" stroke={isRecording ? '#f1ecdf' : 'currentColor'} fill={isRecording ? '#f1ecdf' : 'none'} />
                    <path d="M5 11a7 7 0 0014 0M12 18v3" stroke={isRecording ? '#f1ecdf' : 'currentColor'} />
                  </svg>
                  <span>{isRecording ? 'Recording...' : 'Voice'}</span>
                </button>
              )}
              {isThinking ? (
                <button className={styles.stopBtn} onClick={handleStop} title="Stop">
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor">
                    <rect x="4" y="4" width="16" height="16" rx="2" />
                  </svg>
                </button>
              ) : (
                <button className={styles.sendBtn} onClick={handleSend} disabled={!canSend} title="Send">
                  <span>Send</span>
                  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Common emojis
const EMOJIS = [
  '😀','😂','😊','😍','😎','🤔','👍','👏','🔥','❤️','✅','🚀',
  '😅','😭','🥺','😱','🤗','🤝','🥳','🤖','👀','💪','💡','🙏',
  '💯','⭐','❌','🎉','📎','💻','📱','🔗','🔍','📝','💬','🔔',
];

function EmojiPicker({ onSelect, onClose }: { onSelect: (emoji: string) => void; onClose: () => void }) {
  return (
    <div className={styles.emojiPicker} onClick={(e: MouseEvent) => e.stopPropagation()}>
      <div className={styles.emojiGrid}>
        {EMOJIS.map((e) => (
          <button key={e} className={styles.emojiItem} onClick={() => onSelect(e)}>{e}</button>
        ))}
      </div>
    </div>
  );
}
